import { http } from '@/lib/axios';
import { unwrap, toApiError, getPage } from '@/lib/unwrap';
import type { ApiEnvelope, Page, SearchParams } from '@/types/api';
import type { CatalogProduct, Product, ResolvedPrices } from '@/types/product';
import { SOURCE_CHANNEL } from '@/lib/salesChannel';

export interface ProductListResult {
  items: Product[];
  page?: Page;
}

/** Backend product row (superset of the FE Product) — carries `requiresTimeSlot`. */
type RawProduct = Product & { requiresTimeSlot?: boolean };

/**
 * Normalize a backend product into the FE shape. The backend exposes the
 * slot requirement as `requiresTimeSlot`; the app reads `requiresSlot`. Add-on
 * availability is derived from an embedded `addOns` list when present.
 *
 * `attractionId` + `maxPurchasePerSlot` come stamped by the catalog read
 * (`attachAttractionPurchaseCap`, `-1` = unlimited); they drive the per-slot
 * purchase-cap UX gate. Absent → unlimited (handled by `capOfProduct`).
 */
function normalizeProduct(raw: RawProduct): Product {
  const addOns = Array.isArray(raw.addOns)
    ? raw.addOns.map((a) => ({
        ...a,
        attractionId: a.attractionId ?? raw.attractionId,
        maxPurchasePerSlot: a.maxPurchasePerSlot ?? raw.maxPurchasePerSlot,
      }))
    : [];
  return {
    ...raw,
    requiresSlot: raw.requiresSlot ?? raw.requiresTimeSlot === true,
    hasAddOns: raw.hasAddOns ?? addOns.length > 0,
    attractionId: raw.attractionId,
    maxPurchasePerSlot: raw.maxPurchasePerSlot,
    addOns,
  };
}

export async function getProducts(params?: SearchParams): Promise<ProductListResult> {
  try {
    const res = await http.get<ApiEnvelope<RawProduct[]>>('/product', { params });
    const data = unwrap(res.data);
    const items = (Array.isArray(data) ? data : []).map(normalizeProduct);
    return { items, page: getPage(data) };
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * GET /product/catalog — the products on sale at the counter for one visit
 * date, each with the simple promos a live campaign gives it that day. The
 * backend leaves out a slot ticket with nothing bookable that day. The
 * cashier's login decides the posType; `channel` is what a promo is judged on.
 */
export async function getCatalog(
  visitDate: string,
  opts: { ticketCategory?: 'main' | 'attraction' } = {},
): Promise<CatalogProduct[]> {
  try {
    const res = await http.get<ApiEnvelope<Array<RawProduct & Partial<CatalogProduct>>>>(
      '/product/catalog',
      {
        params: {
          visitDate,
          channel: SOURCE_CHANNEL,
          ticketCategory: opts.ticketCategory,
          lang: 'id',
        },
      },
    );
    const data = unwrap(res.data);
    return (Array.isArray(data) ? data : []).map((raw) => ({
      ...normalizeProduct(raw),
      promoPrice: typeof raw.promoPrice === 'number' ? raw.promoPrice : null,
      promoReplaceable: raw.promoReplaceable === true,
      promos: Array.isArray(raw.promos) ? raw.promos : [],
    }));
  } catch (error) {
    throw toApiError(error);
  }
}

/** POST /product/resolve-prices — slot-aware prices for a visit date. */
export async function resolveProductPrices(
  items: Array<{ productId: string; slotId?: string }>,
  date: string,
): Promise<ResolvedPrices> {
  try {
    const res = await http.post<ApiEnvelope<ResolvedPrices>>('/product/resolve-prices', {
      items,
      date,
      // The channel a promo is judged on; the cashier's login decides the rest.
      channel: SOURCE_CHANNEL,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
